import { useEffect, useRef } from "react";
import type { RefObject } from "@react-types/shared";

/**
 * Checks if the ResizeObserver API is supported.
 * @returns True if the ResizeObserver API is supported, false otherwise.
 */
function hasResizeObserver() {
    return typeof window.ResizeObserver !== "undefined";
}

/**
 * The options for the useResizeObserver hook.
 */
type useResizeObserverOptionsType<T> = {
    /**
     * The ref to the element to observe.
     */
    ref: RefObject<T | undefined | null> | undefined;
    /**
     * The box to observe.
     */
    box?: ResizeObserverBoxOptions;
    /**
     * The callback function to call when the size changes.
     * Note: Does not need to be memoized - internally stabilized via ref.
     */
    onResize: () => void;
};

/**
 * A hook that observes the size of an element and calls a callback function when the size changes.
 * @param options - The options for the hook.
 */
export function useResizeObserver<T extends Element>(options: useResizeObserverOptionsType<T>) {
    const { ref, box, onResize } = options;

    // Store onResize in a ref to avoid recreating observer when callback changes
    const onResizeRef = useRef(onResize);

    // Update ref in effect to avoid ref mutation during render
    useEffect(() => {
        onResizeRef.current = onResize;
    }, [onResize]);

    useEffect(() => {
        const element = ref?.current;
        if (!element) {
            return;
        }

        const handleResize = () => {
            onResizeRef.current();
        };

        if (!hasResizeObserver()) {
            window.addEventListener("resize", handleResize, false);

            return () => {
                window.removeEventListener("resize", handleResize, false);
            };
        } else {
            const resizeObserverInstance = new window.ResizeObserver((entries) => {
                if (!entries.length) {
                    return;
                }

                handleResize();
            });

            resizeObserverInstance.observe(element, { box });

            return () => {
                if (element) {
                    resizeObserverInstance.unobserve(element);
                }
            };
        }
    }, [ref, box]);
}
